import React, { Fragment } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { ExpandMore } from "@mui/icons-material";

import {
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  List,
  Tooltip,
} from "@mui/material";

export default function NavItems(props) {
  const {
    navItems,
    open: openedSideMenu,
    onToggleMenuList: onOpen,
    toggledMenuList: open,
    openPopup,
    buttonRef,
    isDarkMode,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();

  // Theme-responsive colors
  const colors = {
    text: isDarkMode ? "#cbd5e1" : "#475569",
    textActive: isDarkMode ? "#fff" : "#1e293b",
    textMuted: isDarkMode ? "#94a3b8" : "#64748b",
    iconMuted: isDarkMode ? "#64748b" : "#94a3b8",
    iconActive: "#FB923C",
    hoverBg: isDarkMode ? "rgba(234, 88, 12, 0.08)" : "rgba(234, 88, 12, 0.06)",
    hoverText: isDarkMode ? "#e2e8f0" : "#1e293b",
    activeBg: isDarkMode ? "rgba(234, 88, 12, 0.15)" : "rgba(234, 88, 12, 0.12)",
    subText: isDarkMode ? "#94a3b8" : "#64748b",
    subTextActive: isDarkMode ? "#e2e8f0" : "#1e293b",
  };

  const handleItemClick = (item) => {
    if (openedSideMenu) {
      onOpen(item.id);
      // Also navigate to the parent route
      if (item.to) {
        navigate(item.to);
      }
    } else {
      openPopup(item, buttonRef.current);
    }
  };

  return (
    <List sx={{ py: 1 }}>
      {navItems.map((item, itemIdx) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.to;
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const isExpanded = open[item.id];

        const mainButton = (
          <ListItemButton
            component={item.to && !hasSubItems ? NavLink : "div"}
            to={!hasSubItems ? item.to : undefined}
            onClick={hasSubItems ? () => handleItemClick(item) : undefined}
            sx={{
              justifyContent: openedSideMenu ? "flex-start" : "center",
              px: openedSideMenu ? 2 : 1.5,
              py: 1,
              mx: 1,
              mb: 0.5,
              minHeight: 44,
              borderRadius: "8px",
              transition: "all 0.15s ease",
              color: colors.textMuted,
              "&:hover": {
                background: colors.hoverBg,
                color: colors.hoverText,
              },
              "&.active": {
                background: colors.activeBg,
                color: colors.textActive,
                "& .MuiListItemIcon-root": {
                  color: colors.iconActive,
                },
              },
              ...(isActive && !hasSubItems && {
                background: colors.activeBg,
                color: colors.textActive,
              }),
            }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: openedSideMenu ? 32 : "unset",
                  justifyContent: "center",
                  color: isActive ? colors.iconActive : colors.iconMuted,
                }}
              >
                <Icon sx={{ fontSize: "1.2rem" }} />
              </ListItemIcon>
            )}
            {openedSideMenu && (
              <>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: "0.85rem",
                    fontWeight: isActive || isExpanded ? 600 : 500,
                    color: isActive ? colors.textActive : colors.text,
                  }}
                />
                {hasSubItems && (
                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    style={{ display: "flex" }}
                  >
                    <ExpandMore sx={{ fontSize: "1.1rem", color: colors.iconMuted }} />
                  </motion.div>
                )}
              </>
            )}
          </ListItemButton>
        );

        return (
          <Fragment key={itemIdx}>
            {openedSideMenu ? (
              mainButton
            ) : (
              <Tooltip title={item.label} placement="right" arrow>
                {mainButton}
              </Tooltip>
            )}

            {/* Sub Items Accordion */}
            {hasSubItems && openedSideMenu && (
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <List component="div" disablePadding sx={{ pl: 2 }}>
                        {item.subItems.map((subItem, subIdx) => {
                          const SubIcon = subItem.SubIcon;
                          const isSubActive = location.pathname === subItem.to;

                          return (
                            <ListItemButton
                              key={subIdx}
                              component={NavLink}
                              to={subItem.to}
                              sx={{
                                py: 0.75,
                                px: 2,
                                mx: 1,
                                mb: 0.25,
                                minHeight: 36,
                                borderRadius: "6px",
                                borderLeft: "2px solid",
                                borderColor: isSubActive ? "#F97316" : "transparent",
                                background: isSubActive ? colors.activeBg : "transparent",
                                "&:hover": {
                                  background: colors.hoverBg,
                                },
                                "&.active": {
                                  borderColor: "#F97316",
                                  background: colors.activeBg,
                                },
                              }}
                            >
                              {SubIcon && (
                                <ListItemIcon sx={{ minWidth: 28, color: isSubActive ? colors.iconActive : colors.iconMuted }}>
                                  <SubIcon sx={{ fontSize: "0.95rem" }} />
                                </ListItemIcon>
                              )}
                              <ListItemText
                                primary={subItem.label}
                                primaryTypographyProps={{
                                  fontSize: "0.8rem",
                                  fontWeight: isSubActive ? 600 : 400,
                                  color: isSubActive ? colors.subTextActive : colors.subText,
                                }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Collapse>
            )}
          </Fragment>
        );
      })}
    </List>
  );
}
